import { useState } from "react";
import { api, formatApiError } from "@/lib/kfmApi";
import { savePassage, getPassage } from "@/lib/passageCache";
import { noteSnippetText, downloadNoteImage } from "@/lib/shareNote";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import CopyButton from "@/components/CopyButton";
import SermonPanel from "@/components/SermonPanel";
import { Search, NotebookPen, BookOpen, Languages, Loader2, Check, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

const READER_TRANSLATIONS = [
  { id: "niv", short: "NIV" }, { id: "web", short: "WEB" },
  { id: "kjv", short: "KJV" }, { id: "asv", short: "ASV" },
  { id: "nkjv", short: "NKJV" }, { id: "amp", short: "AMP" },
];

export default function StudyTools({ session, onNotesChange }) {
  const passage = session.passage || {};
  const [readerT, setReaderT] = useState(passage.translation || "niv");
  const [verses, setVerses] = useState(passage.verses || []);
  const [readerName, setReaderName] = useState(passage.translation_name || "");
  const [loadingReader, setLoadingReader] = useState(false);

  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);

  const [notes, setNotes] = useState(session.notes || "");
  const [savedNotes, setSavedNotes] = useState(false);

  const switchTranslation = async (t) => {
    setReaderT(t);
    setLoadingReader(true);
    const ref = passage.reference || session.study?.passage_reference;
    try {
      const { data } = await api.get("/bible/passage", {
        params: { reference: ref, translation: t },
      });
      if (data.ok) {
        setVerses(data.verses);
        setReaderName(data.translation_name);
        savePassage(ref, t, data);
      } else toast.error("Couldn't load that translation");
    } catch (e) {
      // Offline fallback: show the cached passage if we have it (item 6).
      const cached = getPassage(ref, t);
      if (cached) {
        setVerses(cached.verses);
        setReaderName(cached.translation_name || "");
        toast.info("Offline — showing the saved passage text.");
      } else {
        toast.error(formatApiError(e.response?.data?.detail));
      }
    }
    setLoadingReader(false);
  };

  const runSearch = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;
    setSearching(true);
    try {
      const { data } = await api.get("/bible/search", {
        params: { query: query.trim(), translation: readerT },
      });
      setResults(data.results || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setSearching(false);
  };

  const saveNotes = async () => {
    try {
      await api.put(`/study/sessions/${session.id}/notes`, { text: notes });
      onNotesChange?.(notes);
      setSavedNotes(true);
      setTimeout(() => setSavedNotes(false), 2000);
    } catch {}
  };

  const readerText = verses.map((v) => `${v.verse} ${v.text}`).join(" ");

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Reader */}
      <section className="rounded-2xl bg-[#0E131F] border border-[#1E273D] p-5 space-y-4" data-testid="tool-reader">
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-slate-500">
            <BookOpen className="w-4 h-4" strokeWidth={1.5} /> Reader
          </span>
          <div className="flex items-center gap-2">
            <CopyButton text={readerText} label="Scripture copied" testid="tool-copy-scripture" />
            <Select value={readerT} onValueChange={switchTranslation}>
              <SelectTrigger data-testid="reader-translation" className="h-9 w-24 rounded-lg border-[#29344D] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0E131F]">
                {READER_TRANSLATIONS.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.short}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="text-xs text-[#00E5FF]">{session.study?.passage_reference} · {readerName}</div>
        {loadingReader ? (
          <Loader2 className="w-5 h-5 animate-spin text-[#00E5FF]" />
        ) : (
          <div className="text-xl leading-[1.9] text-white">
            {verses.map((v, i) => (
              <span key={i}>
                <sup className="text-[#00E5FF] text-xs font-sans mr-1 align-super">{v.verse}</sup>
                {v.text}{" "}
              </span>
            ))}
          </div>
        )}
      </section>

      {/* Verse search */}
      <section className="rounded-2xl bg-[#0E131F] border border-[#1E273D] p-5 space-y-4" data-testid="tool-search">
        <span className="flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-slate-500">
          <Search className="w-4 h-4" strokeWidth={1.5} /> Search Scripture
        </span>
        <form onSubmit={runSearch} className="flex gap-2">
          <Input
            data-testid="search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="A word (grace) or reference (John 3:16)"
            className="h-11 rounded-xl border-[#29344D]"
          />
          <Button data-testid="search-submit" type="submit" disabled={searching}
            className="h-11 rounded-xl bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] px-4">
            {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          </Button>
        </form>
        {results && (
          <div data-testid="search-results" className="space-y-3 max-h-80 overflow-y-auto">
            {results.length === 0 && <p className="text-sm text-slate-500">No matches found.</p>}
            {results.map((r, i) => (
              <div key={i} className="border-b border-[#1E273D] pb-3 last:border-0">
                <div className="text-xs text-[#00E5FF] mb-1">{r.reference}</div>
                <p className="text-base text-slate-300 leading-relaxed">{r.text}</p>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Notes */}
      <section className="rounded-2xl bg-[#0E131F] border border-[#1E273D] p-5 space-y-3" data-testid="tool-notes">
        <span className="flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-slate-500">
          <NotebookPen className="w-4 h-4" strokeWidth={1.5} /> Your notes
        </span>
        <Textarea
          data-testid="notes-input"
          value={notes}
          onChange={(e) => { setNotes(e.target.value); onNotesChange?.(e.target.value); }}
          onBlur={saveNotes}
          placeholder="Your own observations on this passage…"
          rows={6}
          className="rounded-xl border-[#29344D] resize-none focus-visible:ring-[#00E5FF]/30"
        />
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-500 flex items-center gap-1">
            {savedNotes && (<><Check className="w-3 h-3 text-[#00E5FF]" strokeWidth={2} /> Saved</>)}
          </span>
          <div className="flex items-center gap-2">
            {notes.trim() && (
              <>
                <CopyButton
                  text={noteSnippetText(session.study?.passage_reference, notes)}
                  label="Note copied"
                  testid="share-note-copy"
                />
                <Button
                  data-testid="share-note-image"
                  onClick={() => downloadNoteImage(session.study?.passage_reference, notes)}
                  variant="outline"
                  className="h-9 rounded-full border-[#29344D] text-sm"
                >
                  <ImageIcon className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Image
                </Button>
              </>
            )}
            <Button onClick={saveNotes} variant="outline" className="h-9 rounded-full border-[#29344D] text-sm">
              Save notes
            </Button>
          </div>
        </div>
      </section>

      {/* Study → Sermon (paid creation tool, feature-flagged; renders nothing when off) */}
      <SermonPanel session={session} />

      <section className="rounded-2xl bg-[#141B29]/70 border border-[#1E273D] p-5" data-testid="tool-concordance-note">
        <span className="flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-slate-500">
          <Languages className="w-4 h-4" strokeWidth={1.5} /> Original languages
        </span>
        <p className="text-sm text-slate-400 mt-2 leading-relaxed">
          The Strong's Concordance word studies for this passage are in the study above — expand any entry to
          see the Hebrew or Greek behind the text. A "Related passages" section further down the study
          suggests connected passages to explore. Standalone Strong's-number lookup is coming soon.
        </p>
      </section>
    </div>
  );
}
